import { XIcon } from '@commander/ui';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { StepProps } from '../../types/onboarding.types';

const SizeStep: React.FC<StepProps> = ({
  data,
  onChange,
  validation,
  onNext,
  isLoading,
  error,
}) => {
  const sizeOptions = ['1-5', '5-30', '30+'];
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setLocalError(null);
  }, []);

  const handleSizeToggle = (size: string) => {
    let updatedSize: string[];

    if (data.size.includes(size)) {
      // Remove the size
      updatedSize = data.size.filter((s) => s !== size);
    } else {
      // Add the size
      updatedSize = [...data.size, size];
    }

    onChange({ ...data, size: updatedSize });
    setLocalError(null);
  };

  const handleSubmit = async () => {
    if (data.size.length === 0) {
      setLocalError('Please select at least one company size');
      return;
    }

    const isValid = await validation(data);
    if (isValid) {
      setLocalError(null);
      onNext();
    } else {
      setLocalError('Please select at least one company size');
    }
  };

  return (
    <div className="space-y-6">
      {/* Size Options - Multiple Selection */}
      <div className="space-y-4">
        <div className="flex flex-col gap-3 max-w-[60px] w-fit">
          {sizeOptions.map((size) => (
            <label
              key={size}
              className="flex items-center space-x-3 cursor-pointer hover:bg-gray-300 transition-colors duration-300 bg-[#EEF0F2] p-2 rounded-lg"
              onClick={() => handleSizeToggle(size)}
            >
              <span className="text-heading-4 text-gray-700 font-paragraph">{size}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Added Services Display */}
      {data.size?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Selected Sizes ({data.size.length})
          </h3>
          <div className="flex flex-wrap gap-2 max-h-[250px] overflow-y-auto font-paragraph text-heading-4">
            {data.size.map((size, index) => (
              <div key={`${size}-${index}`} className="flex items-center m-1">
                <div className="bg-primary text-white rounded-l-[4px] px-3 py-1 flex items-center">
                  {size} employees
                </div>
                <button
                  onClick={() => {
                    const updatedSize = data.size.filter((s) => s !== size);
                    onChange({ ...data, size: updatedSize });
                  }}
                  className="bg-primary text-white rounded-r-md ml-[2px] px-[4px] py-1 flex items-center hover:bg-red-700 transition-colors"
                >
                  <XIcon className="w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {(error || localError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || localError}
        </div>
      )}

      {/* Hidden navigation for parent component to handle */}
      <div className="hidden">
        <button onClick={handleSubmit} disabled={isLoading || data.size.length === 0} />
      </div>
    </div>
  );
};

export default SizeStep;
